import { FormEvent, FunctionComponent } from 'react';
import { ErrorOption, FieldErrors, UseFormRegister, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import classNames from 'classnames';

import { Button, Form, FormFeedback, FormGroup, Tooltip } from '../../../components';
import { useSettings } from '../../../providers/SettingsProvider/SettingsProvider';
import useNavigateWithSearchParams from '../../../behaviors/use-navigate-with-search-params/use-navigate-with-search-params';
import signInFormErrorMap, { TEACHER_LOGIN_ON_STUDENT_PAGE } from './SignInForm.error-codes';
import handleError from '../../../utils/error-handler/error-handler';
import { usePlatform } from '../../../providers/PlatformProvider/PlatformProvider';
import useUserContextApi from '../../../behaviors/api-hooks/use-user-context-api/use-user-context-api';
import { fetchWithToken } from '../../../utils/api/api';
import { UserRoles } from '../../../utils/shape';
import useClasses from '../../../behaviors/use-classes/use-classes';
import { useWindowQuery } from '../../../providers/ResponsivityProvider/ResponsivityProvider';
import { useDpState } from '../../../providers/DpStateProvider/DpStateProvider';

import './SignInForm.scss';

export interface SignInFormData extends Record<string, string> {
  username: string;
  password: string;
}

const schema = yup.object().shape({
  username: yup.string().required('Username is required.'),
  password: yup.string().required('Password is required.'),
});

interface SignInFormInputProps {
  baseClass: string;
  errors: FieldErrors<SignInFormData>;
  register: UseFormRegister<SignInFormData>;
}

interface SignInUsernameInputProps extends SignInFormInputProps {
  role?: UserRoles;
}

interface SignInPasswordInputProps extends SignInFormInputProps {
  setError: (name: string, error: ErrorOption) => void;
}

const UsernameInput = ({ baseClass, role, errors, register }: SignInUsernameInputProps) => {
  return (
    <>
      <label className={`${baseClass}__input-label`} htmlFor="username" id="sign-in-username">
        <span className={`${baseClass}__input-label-text`}>
          {role && role !== 'student' ? 'Email' : 'Username'}
        </span>
        <input
          {...register('username')}
          aria-labelledby="sign-in-username"
          className={classNames(`vol-input ${baseClass}__input`, {
            'vol-input--invalid': errors.username,
          })}
          name="username"
          placeholder={`Enter ${role && role !== 'student' ? 'email' : 'username'}`}
          type={role && role !== 'student' ? 'email' : 'text'}
          data-testid="usernameInput"
        />
      </label>
      {errors.username && (
        <FormFeedback data-testid="usernameFeedback" className={`${baseClass}__feedback`}>
          {errors.username?.message}
        </FormFeedback>
      )}
    </>
  );
};

UsernameInput.defaultProps = {
  role: undefined,
};

const PasswordInput = ({ baseClass, errors, setError, register }: SignInPasswordInputProps) => {
  const validateInput = (e: FormEvent<HTMLInputElement>) => {
    const inputValue = (e?.target as HTMLInputElement)?.value;
    if (!inputValue) {
      setError('password', { message: 'Password is required.' });
    }
  };

  return (
    <>
      <label className={`${baseClass}__input-label`} htmlFor="password" id="sign-in-password">
        <span className={`${baseClass}__input-label-text`}>Password</span>

        <input
          {...register('password')}
          aria-labelledby="sign-in-password"
          className={classNames(`vol-input ${baseClass}__input`, {
            'vol-input--invalid': errors.password,
          })}
          name="password"
          placeholder="Enter password"
          type="password"
          data-testid="passwordInput"
          onBlur={(e) => validateInput(e)}
        />
      </label>
      <FormFeedback data-testid="passwordFeedback" className={`${baseClass}__feedback`}>
        {errors.password?.message}
      </FormFeedback>
    </>
  );
};

const baseClass = 'customer-portal-sign-in-form';

const SignInForm: FunctionComponent = () => {
  const { navigateWithSearchParams } = useNavigateWithSearchParams();

  const { role } = useDpState();
  const { googleRecaptchaEnabled } = useSettings();
  const { track } = usePlatform();

  const { responsiveClassName } = useWindowQuery({ className: baseClass });
  const classes = useClasses({ baseClass }, [responsiveClassName]);

  const {
    register,
    handleSubmit,
    setError,
    formState: { isValid, errors },
  } = useForm<SignInFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldUnregister: false,
    resolver: yupResolver(schema),
  });

  const { loginUser } = useUserContextApi({});

  const onSubmit = async (formData: SignInFormData) => {
    const params = { ...formData };

    const submit = async (token?: string) => {
      if (token) {
        params['g-recaptcha-response'] = token;
      }

      const response = await fetchWithToken(`login/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(params),
      });

      const data = await response.json();

      if (data.userContext) {
        await loginUser();
        navigateWithSearchParams('/');
        return;
      }

      const errorMessage = data.code;
      track('error-message-shown', { errorMessage });
      handleError(signInFormErrorMap, data);

      if (errorMessage === TEACHER_LOGIN_ON_STUDENT_PAGE) {
        navigateWithSearchParams('/sign-in/staff');
      }
    };

    if (googleRecaptchaEnabled) {
      grecaptcha.ready(() => {
        return grecaptcha
          .execute('6LcWqkgaAAAAAL8IQnmcS8z65iJYy5FYvXgcfInM', {
            action: 'submit',
          })
          .then(submit);
      });
    } else {
      await submit();
    }
  };

  return (
    <Form className={classes} onSubmit={handleSubmit(onSubmit)} data-testid="loginForm">
      <FormGroup>
        <UsernameInput
          role={role || undefined}
          baseClass={baseClass}
          errors={errors}
          register={register}
        />
      </FormGroup>

      <FormGroup className={`${baseClass}__input-container`}>
        <PasswordInput
          baseClass={baseClass}
          errors={errors}
          register={register}
          setError={setError}
        />
        {role === 'teacher' ? (
          <Button
            className={`${baseClass}__link`}
            color="default"
            link
            href="https://account.scholastic.com/my-scholastic/sign-in/forgot-password.html"
          >
            Forgot password?
          </Button>
        ) : (
          <>
            <Button
              className={`${baseClass}__link`}
              color="default"
              link
              href=""
              data-for="forgotPasswordTooltip"
              data-tip="If you don't know your username or password, ask your educator or librarian and they can find it for you."
              data-testid="tooltip-button"
              onClick={() => track('forgot-password', {})}
            >
              Forgot password?
            </Button>
            <Tooltip
              effect="solid"
              id="forgotPasswordTooltip"
              place="bottom"
              uuid="forgot-password"
              data-testid="forgotPasswordTooltip"
            />
          </>
        )}
      </FormGroup>

      <Button
        className={`${baseClass}__submit-button`}
        aria-label="Sign In"
        color="primary"
        disabled={!isValid}
        type="submit"
        data-testid="submitButton"
      >
        SIGN IN
      </Button>
    </Form>
  );
};

export default SignInForm;
